import { Edit, useForm } from "@refinedev/antd";
import { Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Spin, Switch } from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useShow } from "@refinedev/core";

// Define interfaces for our data structures
interface PriceRecord {
    id: number;
    inventoryItemId: number;
    itemCode: string;
    basePrice: number;
    discountType: string;
    discountValue: number;
    minimumQuantity: number;
    maximumQuantity: number;
    effectivePrice: number;
    startDate: string | null;
    endDate: string | null;
    isActive: boolean;
    currencyCode: string;
    createdAt: string;
    updatedAt: string;
    inventoryItem: {
        id: number;
        itemName: string;
        itemCode: string;
        itemClassificationCode: string;
        itemTypeCode: string;
        barcode: string;
        isActive: boolean;
        additionalInformation: string;
    };
    [key: string]: any; // Allow for flexible property access
}

export const PriceListEdit: React.FC = () => {
    // Get the ID from the URL
    const { id } = useParams();
    const [debugInfo, setDebugInfo] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);

    console.log("PriceListEdit - ID from URL params:", id);

    // Use useShow to fetch data directly
    const { queryResult } = useShow({
        resource: "price",
        id,
        meta: {
            fields: [
                "id",
                "inventoryItemId",
                "itemCode",
                "basePrice",
                "discountType",
                "discountValue",
                "minimumQuantity",
                "maximumQuantity",
                "effectivePrice",
                "startDate",
                "endDate",
                "isActive",
                "currencyCode",
                "createdAt",
                "updatedAt",
                {
                    inventoryItem: [
                        "id",
                        "itemName",
                        "itemCode",
                        "itemClassificationCode",
                        "itemTypeCode",
                        "barcode",
                        "isActive",
                        "additionalInformation"
                    ]
                }
            ]
        }
    });

    // Get form instance for the edit form
    const { formProps, saveButtonProps } = useForm({
        resource: "price",
        id,
        action: "edit",
        redirect: "list", // Redirect to price list after successful save
        // Transform data before submission to match API expectations
        onMutationSuccess: (data: any) => {
            console.log("Form submitted successfully:", data);
        },
        meta: {
            fields: [
                "id",
                "inventoryItemId",
                "itemCode",  // Important to include this for the API
                "basePrice",
                "discountType",
                "discountValue",
                "minimumQuantity",
                "maximumQuantity",
                "effectivePrice",
                "startDate",
                "endDate",
                "isActive",
                "currencyCode"
            ]
        },
        // Transform the form values before submission
        transformValues: (values: Record<string, any>) => {
            console.log("Form values before transform:", values);

            // Create a new object with the correct field mapping
            const transformed = {
                ...values,
                // Make sure itemCode is included (API requires this field)
                itemCode: values.selectedItemCode || values.itemCode,
                // Format dates properly for the API
                startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : null,
                endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : null,
            };

            console.log("Transformed values for submission:", transformed);
            return transformed;
        }
    });

    // Log debugging information about query state
    useEffect(() => {
        const { data, isLoading, isFetching } = queryResult;
        setLoading(isLoading);
    }, [id, queryResult]);

    // When data is loaded, populate the form
    useEffect(() => {
        const record = queryResult?.data?.data as PriceRecord | undefined;

        if (record && formProps.form) {
            // Log the loaded data to help with debugging
            console.log("Loaded price record:", record);

            try {
                // Get itemCode from the inventoryItem object if it exists, otherwise from record
                const itemCode = record.inventoryItem?.itemCode || record.itemCode || '';
                
                // Set form values
                formProps.form.setFieldsValue({
                    ...record,
                    // Convert dates to dayjs objects
                    startDate: record.startDate ? dayjs(record.startDate) : null,
                    endDate: record.endDate ? dayjs(record.endDate) : null,
                    // Store itemCode from inventoryItem for display and submission
                    selectedItemCode: itemCode,
                    itemCode: itemCode,
                    // Set inventory item fields
                    inventoryItem: record.inventoryItem || {}
                });

                console.log("Form values set successfully with itemCode:", itemCode);

                // Update effective price calculation
                updateEffectivePrice();
            } catch (error) {
                console.error("Error setting form values:", error);
            }
        } else {
            console.log("No record data available yet or form not initialized");
        }
    }, [queryResult?.data?.data, formProps.form]);

    // Helper function to calculate effective price
    const calculateEffectivePrice = (basePrice: number, discountType: string, discountValue: number) => {
        if (discountType === "None" || !discountValue) return basePrice;
        if (discountType === "Percentage") {
            return basePrice - (basePrice * (discountValue / 100));
        }
        if (discountType === "FixedAmount") {
            return Math.max(0, basePrice - discountValue);
        }
        return basePrice;
    };

    // Update effective price when relevant fields change
    const updateEffectivePrice = () => {
        const form = formProps.form;
        if (!form) return;

        const basePrice = form.getFieldValue('basePrice') || 0;
        const discountType = form.getFieldValue('discountType') || "None";
        const discountValue = form.getFieldValue('discountValue') || 0;

        const effectivePrice = calculateEffectivePrice(basePrice, discountType, discountValue);

        form.setFieldsValue({ effectivePrice });
    };

    // Handler for form field changes
    const handleFormFieldChange = (changedValues: Record<string, any>) => {
        console.log("Form values changed:", changedValues);

        // If selectedItemCode changed, update the hidden itemCode field
        if ('selectedItemCode' in changedValues) {
            formProps.form?.setFieldsValue({
                itemCode: changedValues.selectedItemCode
            });
        }

        updateEffectivePrice();
    };

    // If loading, show spinner
    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Edit saveButtonProps={saveButtonProps}>
            <Form
                {...formProps}
                layout="vertical"
                onValuesChange={handleFormFieldChange}
            >
                <Card title="Edit Price">
                    <Form.Item name="id" hidden>
                        <InputNumber min={0} max={999999999} />
                    </Form.Item>

                    {/* Hidden field to preserve the inventoryItemId */}
                    <Form.Item name="inventoryItemId" hidden>
                        <InputNumber min={0} max={999999999} />
                    </Form.Item>

                    {/* Hidden field for the actual itemCode that will be sent to API */}
                    <Form.Item name="itemCode" hidden>
                        <Input id="itemCodeHidden" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                label="Item Code"
                                name="selectedItemCode"
                                rules={[{ required: true, message: 'Missing item code' }]}
                            >
                                <Input disabled id="selectedItemCode" />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item
                                label="Item Name"
                                name={['inventoryItem', 'itemName']}
                            >
                                <Input disabled id="itemName" />
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item
                                label="Item Type"
                                name={['inventoryItem', 'itemTypeCode']}
                            >
                                <Input disabled id="itemType" />
                            </Form.Item>
                        </Col>

                        <Col xs={12}>
                            <Form.Item
                                label="Classification"
                                name={['inventoryItem', 'itemClassificationCode']}
                            >
                                <Input disabled id="itemClassification" />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="basePrice"
                                label="Base Price"
                                rules={[{ required: true, message: 'Base price is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    onChange={updateEffectivePrice}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="discountType"
                                label="Discount Type"
                                rules={[{ required: true, message: 'Discount type is required' }]}
                            >
                                <Select
                                    style={{ width: "100%" }}
                                    options={[
                                        { label: "None", value: "None" },
                                        { label: "Percentage", value: "Percentage" },
                                        { label: "Fixed Amount", value: "FixedAmount" }
                                    ]}
                                    onChange={() => updateEffectivePrice()}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="discountValue"
                                label="Discount Value"
                                rules={[{ required: true, message: 'Discount value is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    style={{ width: "100%" }}
                                    onChange={() => updateEffectivePrice()}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="effectivePrice"
                                label="Effective Price"
                            >
                                <InputNumber
                                    disabled
                                    min={0}
                                    max={999999999}
                                    precision={2}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="minimumQuantity"
                                label="Min Quantity"
                                rules={[{ required: true, message: 'Minimum quantity is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="maximumQuantity"
                                label="Max Quantity"
                                rules={[{ required: true, message: 'Maximum quantity is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={999999999}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="currencyCode"
                                label="Currency"
                                rules={[{ required: true, message: 'Currency is required' }]}
                            >
                                <Input id="currencyCode" readOnly />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="startDate"
                                label="Start Date"
                                rules={[{ required: true, message: 'Start date is required' }]}
                                getValueProps={(value: any) => ({
                                    value: value ? dayjs(value) : undefined,
                                })}
                            >
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="endDate"
                                label="End Date"
                                getValueProps={(value: any) => ({
                                    value: value ? dayjs(value) : undefined,
                                })}
                            >
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>

                        <Col xs={8}>
                            <Form.Item
                                name="isActive"
                                label="Active"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Edit>
    );
};